import Cleave from "cleave.js/react";
import classNames from "classnames";
import ReactSelect from "react-select";
import {Form} from "react-bootstrap";
import {useSelector} from "react-redux";
import React, {Fragment} from 'react';
import {Controller, useFormContext} from "react-hook-form";
import {formatBillingMoney} from "../../../../helpers/billing-format";
import {getPackageCodeLabel} from "../../../../enum/BillingPackageCode";
import {selectLang, selectSystemMoneyFormat, Translate} from "../../../../app/store/reducers/main/mainReducer";

/**
 * Chegirma yaratish/tahrirlash formasi.
 *
 * @param {boolean} isEditing tahrirlash rejimi — paketni o'zgartirib bo'lmaydi
 *                            (API faqat amount'ni yangilaydi)
 * @param {Array} packages paketlar katalogi (contracts.PackageContract[])
 * @param {Array} disabledPackageCodes chegirmasi allaqachon mavjud kodlar (409 oldini olish)
 */
const OrganizationPackageDiscountForm = ({isEditing, packages = [], disabledPackageCodes = []}) => {
    const {formState: {errors}, watch} = useFormContext();
    const systemMoneyFormat = useSelector(selectSystemMoneyFormat);
    const lang = useSelector(selectLang);
    const t = Translate;

    const packageCode = watch('packageCode');
    const selectedPackage = packages.find(item => item.code === packageCode) || null;
    const packageCost = selectedPackage?.cost ?? null;

    const formatMoney = (value) => formatBillingMoney(value, systemMoneyFormat);

    const options = isEditing
        ? packages
        : packages.filter(item => !disabledPackageCodes.includes(item.code));

    // Tahrirlash rejimida paket katalogda topilmasa ham nimadir ko'rsatilishi kerak.
    const selectedOption = selectedPackage || (packageCode ? {code: packageCode, name: null} : null);

    const buildOptionLabel = (option) => {
        const parts = [getPackageCodeLabel(option.code, lang, t, option.name)];
        if (option.cost !== null && option.cost !== undefined) parts.push(formatMoney(option.cost));
        if (option.is_demo) parts.push('demo');
        return parts.join(' · ');
    };

    return (
        <Fragment>
            <Form.Group className={'mb-3'}>
                <Form.Label>{t(lang, 'admin.organization.package_discounts.form.package')}</Form.Label>
                <Controller
                    name={'packageCode'}
                    rules={{required: t(lang, 'main.validation.is_required')}}
                    render={({field, fieldState}) => (
                        <ReactSelect
                            isClearable={true}
                            isDisabled={isEditing}
                            hideSelectedOptions={true}
                            classNamePrefix={'react-select'}
                            options={options}
                            value={selectedOption}
                            getOptionValue={(option) => option.code}
                            getOptionLabel={buildOptionLabel}
                            placeholder={t(lang, 'admin.organization.package_discounts.form.package.placeholder')}
                            className={classNames({'is-invalid border border-danger rounded-2': fieldState.error})}
                            onChange={(option) => field.onChange(option ? option.code : null)}
                        />
                    )}
                />
                <Form.Control.Feedback type={'invalid'}>
                    {errors?.packageCode?.message}
                </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
                <Form.Label>{t(lang, 'admin.organization.package_discounts.form.amount')}</Form.Label>
                <Controller
                    name={'amount'}
                    rules={{
                        required: t(lang, 'main.validation.is_required'),
                        validate: (value) => {
                            const amount = Number(value);

                            if (!Number.isFinite(amount) || amount <= 0)
                                return t(lang, 'admin.organization.package_discounts.form.amount.min_error');

                            if (packageCost !== null && packageCost !== undefined && amount > Number(packageCost))
                                return t(lang, 'admin.organization.package_discounts.form.amount.max_error');

                            return true;
                        }
                    }}
                    render={({field}) => (
                        <Cleave
                            value={field.value}
                            htmlRef={field.ref}
                            onChange={(event) => field.onChange(event.target.rawValue)}
                            options={{numeral: true, delimiter: ' ', numeralDecimalScale: 2}}
                            placeholder={t(lang, 'admin.organization.package_discounts.form.amount.placeholder')}
                            className={classNames('form-control', {'is-invalid': errors?.amount})}
                        />
                    )}
                />
                <Form.Control.Feedback type={'invalid'}>
                    {errors?.amount?.message}
                </Form.Control.Feedback>
                {packageCost !== null && packageCost !== undefined && (
                    <Form.Text className={'text-muted'}>
                        {t(lang, 'admin.organization.package_discounts.form.amount.hint')}: {formatMoney(packageCost)}
                    </Form.Text>
                )}
            </Form.Group>
        </Fragment>
    );
};

export default OrganizationPackageDiscountForm;
